import { EOpenAIUserRole, IOpenAICodeGeneratorMessageDTO } from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import MarkedReact from "marked-react";
import React, { createRef, KeyboardEvent, PureComponent, ReactNode } from "react";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import SpinSvg from "../../../../svgs/spin.svg";
import KuikaAvatar, { getIdentitySeed } from "../../../ui-kit/kuika-avatar/kuika-avatar";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaCollapsePanel from "../../../ui-kit/kuika-collapse-panel/kuika-collapse-panel";
import KuikaInfoBox from "../../../ui-kit/kuika-info-box/kuika-info-box";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import "./kuika-chatbot.scss";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";

type FixCategory = "FIX" | "ADD" | "REMOVE" | "IMPROVE" | "INFO";

interface IFixLine {
  category: FixCategory;
  text: string;
}

const FIX_CATEGORY_META: Record<
  FixCategory,
  { color: "red" | "blue" | "yellow" | "gray"; iconName: string; label: string }
> = {
  FIX: { color: "red", iconName: "exclamation-circle", label: "Fixes" },
  ADD: { color: "blue", iconName: "plus", label: "Additions" },
  REMOVE: { color: "yellow", iconName: "trash", label: "Removals" },
  IMPROVE: { color: "blue", iconName: "edit", label: "Improvements" },
  INFO: { color: "gray", iconName: "info-circle", label: "Notes" }
};

// Display order of accordion sections (highest priority first)
const FIX_CATEGORY_ORDER: FixCategory[] = ["FIX", "ADD", "REMOVE", "IMPROVE", "INFO"];

const TAG_REGEX = /\[(FIX|ADD|REMOVE|IMPROVE|INFO)\]/gi;

const cleanFixText = (raw: string): string => {
  return raw
    .replace(/^[-*•\s]+/, "") // strip leading bullets/dashes
    .replace(/\s+/g, " ")
    .trim();
};

const parseFixLines = (content: string): IFixLine[] | null => {
  if (typeof content !== "string") return null;

  // Find every [CATEGORY] tag occurrence and its position
  const matches = Array.from(content.matchAll(TAG_REGEX));
  if (matches.length === 0) return null;

  // Determine tag placement style: if the first non-whitespace char is `[`,
  // tags LEAD each item. Otherwise tags TRAIL each item (LLM put the tag at end).
  const trimmed = content.trimStart();
  const tagsLead = trimmed.startsWith("[");

  const fixes: IFixLine[] = [];

  if (tagsLead) {
    // Pattern: [TAG] text [TAG] text [TAG] text
    for (let i = 0; i < matches.length; i++) {
      const match = matches[i];
      const category = match[1].toUpperCase() as FixCategory;
      const start = match.index! + match[0].length;
      const end = i + 1 < matches.length ? matches[i + 1].index! : content.length;
      const text = cleanFixText(content.substring(start, end));
      if (text) fixes.push({ category, text });
    }
  } else {
    // Pattern: text [TAG] text [TAG] text [TAG] text(orphan)
    let lastIndex = 0;
    for (const match of matches) {
      const text = cleanFixText(content.substring(lastIndex, match.index!));
      const category = match[1].toUpperCase() as FixCategory;
      if (text) fixes.push({ category, text });
      lastIndex = match.index! + match[0].length;
    }
    // Trailing text after the last tag has no category — treat as INFO
    const trailing = cleanFixText(content.substring(lastIndex));
    if (trailing) fixes.push({ category: "INFO", text: trailing });
  }

  return fixes.length ? fixes : null;
};

declare let window: any;

interface IKuikaChatBotProps {
  messagesContainerRef?: React.RefObject<HTMLDivElement>;
  inputPlaceholder?: string;
  initMessage?: string;
  isLoading?: boolean;
  messages: IOpenAICodeGeneratorMessageDTO[];
  inputRightIcon?: ReactNode;
  onMessageSend: (message: string) => void;
}

interface IKuikaChatBotState {}

interface IKuikaMarkdownRendererProps {
  content: string;
}

const KuikaMarkdownRenderer = ({ content }: IKuikaMarkdownRendererProps) => {
  return <MarkedReact value={content} />;
};

export class KuikaChatBot extends PureComponent<IKuikaChatBotProps, IKuikaChatBotState> {
  formRef = createRef<FormInstance<any>>();

  textareaRef = createRef<HTMLTextAreaElement>();

  userPhoto = (
    <KuikaAvatar size={24} src={window.userInfo?.profilePhotoUrl} seed={getIdentitySeed(window.userInfo)}>
      {KMainFunctions.getUserLettersToShow(window.userInfo)}
    </KuikaAvatar>
  );

  constructor(props: IKuikaChatBotProps) {
    super(props);
    this.state = {
      messages: [],
      isLoading: false
    };
  }

  focusInput = () => {
    this.textareaRef.current?.focus();
  };

  onMessageSend = (data) => {
    const message = data.userMessage?.trim();
    if (!message) return;

    this.formRef.current?.resetFields();

    // this.handlePushMessage({ role: EOpenAIUserRole.User, content: data.userMessage });

    this.props.onMessageSend(message);
  };

  handleInputOnEnter = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    e.stopPropagation();

    // Allow Shift+Enter to create new line
    if (e.shiftKey) {
      return;
    }

    // Only submit on Enter without Shift
    if (e.key !== "Enter" || this.props.isLoading) return;
    e.preventDefault();
    this.formRef.current?.submit();
  };

  isConfigSummaryMessage = (message: IOpenAICodeGeneratorMessageDTO): boolean => {
    return (
      message.role === EOpenAIUserRole.User &&
      typeof message.content === "string" &&
      message.content.includes("Current Agent Configuration Summary:")
    );
  };

  renderMessage = (message: IOpenAICodeGeneratorMessageDTO): JSX.Element => {
    let avatar = (
      <KuikaAvatar size={24} src={window.userInfo?.profilePhotoUrl} seed={getIdentitySeed(window.userInfo)}>
        {KMainFunctions.getUserLettersToShow(window.userInfo)}
      </KuikaAvatar>
    );

    let additionalClass = "";
    if (message.role === EOpenAIUserRole.Assistant) {
      additionalClass = "ai-message";

      avatar = (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g clipPath="url(#clip0_12944_38627)">
            <circle cx="12" cy="12" r="12" fill="#F1ECFF" />
            <path
              d="M14.5019 6H9.41501C9.03964 6 8.73535 6.37042 8.73535 6.82736V9.04036C8.73535 9.4973 9.03964 9.86772 9.41501 9.86772H14.5019C14.8773 9.86772 15.1816 9.4973 15.1816 9.04036V6.82736C15.1816 6.37042 14.8773 6 14.5019 6Z"
              fill="#41546E"
            />
            <g filter="url(#filter0_d_12944_38627)">
              <path
                d="M22.0574 18.0331C22.0574 23.492 17.6321 27.9173 12.1732 27.9173C6.71436 27.9173 2.28906 23.492 2.28906 18.0331C2.28906 12.5742 4.86754 8.14893 12.1732 8.14893C19.4789 8.14893 22.0574 12.5742 22.0574 18.0331Z"
                fill="#D9D9D9"
              />
              <path
                d="M22.0574 18.0331C22.0574 23.492 17.6321 27.9173 12.1732 27.9173C6.71436 27.9173 2.28906 23.492 2.28906 18.0331C2.28906 12.5742 4.86754 8.14893 12.1732 8.14893C19.4789 8.14893 22.0574 12.5742 22.0574 18.0331Z"
                fill="url(#paint0_linear_12944_38627)"
              />
            </g>
            <path
              d="M21.6868 20.7751C21.3164 20.9599 20.9018 21.0571 20.4817 21.0571H3.51789C3.09592 21.0571 2.68131 20.9599 2.31277 20.7751C1.40433 20.3194 0.892063 19.4005 1.01921 18.4574L1.02842 18.3883C1.4633 15.1948 4.31762 12.739 7.81503 12.556C9.29471 12.4775 10.8481 12.4383 12.4697 12.4476C13.7393 12.4551 14.9665 12.4925 16.1477 12.5541C19.6598 12.7371 22.5344 15.1836 22.9711 18.3883C22.9748 18.4107 22.9767 18.435 22.9803 18.4574C23.1093 19.4005 22.5952 20.3194 21.6868 20.7751Z"
              fill="#17253C"
            />
            <path
              d="M7.52841 19.4406C8.79645 19.4406 9.8244 18.3987 9.8244 17.1136C9.8244 15.8284 8.79645 14.7866 7.52841 14.7866C6.26037 14.7866 5.23242 15.8284 5.23242 17.1136C5.23242 18.3987 6.26037 19.4406 7.52841 19.4406Z"
              fill="#24FCE2"
            />
            <path
              d="M7.52901 19.0671C8.59351 19.0671 9.45646 18.1925 9.45646 17.1136C9.45646 16.0347 8.59351 15.1602 7.52901 15.1602C6.46451 15.1602 5.60156 16.0347 5.60156 17.1136C5.60156 18.1925 6.46451 19.0671 7.52901 19.0671Z"
              fill="url(#paint1_linear_12944_38627)"
            />
            <path
              d="M8.76391 16.6069C8.76391 17.0066 8.44328 17.3315 8.04895 17.3315C7.65461 17.3315 7.33398 17.0066 7.33398 16.6069C7.33398 16.2073 7.65461 15.8823 8.04895 15.8823C8.44328 15.8823 8.76391 16.2073 8.76391 16.6069Z"
              fill="#EEFDFF"
            />
            <path
              d="M16.169 19.4406C17.4371 19.4406 18.465 18.3987 18.465 17.1136C18.465 15.8284 17.4371 14.7866 16.169 14.7866C14.901 14.7866 13.873 15.8284 13.873 17.1136C13.873 18.3987 14.901 19.4406 16.169 19.4406Z"
              fill="#24FCE2"
            />
            <path
              d="M16.1696 19.0676C17.2341 19.0676 18.0971 18.193 18.0971 17.1141C18.0971 16.0352 17.2341 15.1606 16.1696 15.1606C15.1051 15.1606 14.2422 16.0352 14.2422 17.1141C14.2422 18.193 15.1051 19.0676 16.1696 19.0676Z"
              fill="url(#paint2_linear_12944_38627)"
            />
            <path
              d="M17.4045 16.6069C17.4045 17.0066 17.0839 17.3315 16.6896 17.3315C16.2952 17.3315 15.9746 17.0066 15.9746 16.6069C15.9746 16.2073 16.2952 15.8823 16.6896 15.8823C17.0839 15.8823 17.4045 16.2073 17.4045 16.6069Z"
              fill="#EEFDFF"
            />
            <path
              d="M19.4786 11.6021C19.4887 11.9686 19.1871 12.4065 18.8413 12.444C18.517 12.479 18.2583 12.1513 17.992 11.8123C17.7296 11.4782 17.1541 10.6213 17.3826 10.3786C17.6841 10.0584 19.4546 10.7364 19.4786 11.6008V11.6021Z"
              fill="#D6C8FF"
            />
            <path
              d="M16.6856 9.86774C16.8042 9.86774 16.9005 9.77153 16.9005 9.65286C16.9005 9.53419 16.8042 9.43799 16.6856 9.43799C16.5669 9.43799 16.4707 9.53419 16.4707 9.65286C16.4707 9.77153 16.5669 9.86774 16.6856 9.86774Z"
              fill="#D6C8FF"
            />
          </g>
          <rect
            x="0.137931"
            y="0.137931"
            width="23.7241"
            height="23.7241"
            rx="11.8621"
            stroke="url(#paint3_linear_12944_38627)"
            strokeWidth="0.275862"
          />
          <defs>
            <filter
              id="filter0_d_12944_38627"
              x="-1.69586"
              y="6.15646"
              width="27.7384"
              height="27.7384"
              filterUnits="userSpaceOnUse"
              color-interpolation-filters="sRGB"
            >
              <feFlood flood-opacity="0" result="BackgroundImageFix" />
              <feColorMatrix
                in="SourceAlpha"
                type="matrix"
                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                result="hardAlpha"
              />
              <feOffset dy="1.99246" />
              <feGaussianBlur stdDeviation="1.99246" />
              <feComposite in2="hardAlpha" operator="out" />
              <feColorMatrix type="matrix" values="0 0 0 0 0.141176 0 0 0 0 0.427451 0 0 0 0 1 0 0 0 0.24 0" />
              <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_12944_38627" />
              <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_12944_38627" result="shape" />
            </filter>
            <linearGradient
              id="paint0_linear_12944_38627"
              x1="0.204261"
              y1="38.7394"
              x2="33.6523"
              y2="13.6432"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0.04" stop-color="#9068FF" />
              <stop offset="1" stop-color="#D4C9FF" />
            </linearGradient>
            <linearGradient
              id="paint1_linear_12944_38627"
              x1="5.59972"
              y1="17.1136"
              x2="9.45646"
              y2="17.1136"
              gradientUnits="userSpaceOnUse"
            >
              <stop stop-color="#030D14" />
              <stop offset="1" stop-color="#0B2F45" />
            </linearGradient>
            <linearGradient
              id="paint2_linear_12944_38627"
              x1="14.2403"
              y1="17.1141"
              x2="18.0971"
              y2="17.1141"
              gradientUnits="userSpaceOnUse"
            >
              <stop stop-color="#030D14" />
              <stop offset="1" stop-color="#0B2F45" />
            </linearGradient>
            <linearGradient
              id="paint3_linear_12944_38627"
              x1="-3.31035"
              y1="-6.75862"
              x2="24.1379"
              y2="38.069"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0.253089" stop-color="#C7B3FF" />
              <stop offset="0.410433" stop-color="#246DFF" stop-opacity="0.4" />
              <stop offset="0.430773" stop-color="#C7B3FF" />
              <stop offset="0.809576" stop-color="#C7B3FF" />
              <stop offset="0.849246" stop-color="#246DFF" />
            </linearGradient>
            <clipPath id="clip0_12944_38627">
              <rect width="24" height="24" rx="12" fill="white" />
            </clipPath>
          </defs>
        </svg>
      );
    }

    const isConfigSummaryMessage = this.isConfigSummaryMessage(message);
    const content = typeof message.content === "string" ? message.content : String(message.content || "");

    const fixLines = message.role === EOpenAIUserRole.Assistant ? parseFixLines(content) : null;

    return (
      <div className={`kuika_chatbot__message ${additionalClass}`}>
        {avatar}
        <div className="kuika_chatbot__message-text">
          {fixLines ? this.renderFixGroups(fixLines) : <KuikaMarkdownRenderer content={content} />}
        </div>
      </div>
    );
  };

  renderFixGroups = (fixLines: IFixLine[]): JSX.Element => {
    // Group fixes by category, preserving order within each group
    const grouped = new Map<FixCategory, IFixLine[]>();
    for (const fix of fixLines) {
      if (!grouped.has(fix.category)) grouped.set(fix.category, []);
      grouped.get(fix.category)!.push(fix);
    }

    // Build accordion items in priority order, only for categories that have fixes
    const items = FIX_CATEGORY_ORDER.filter((cat) => grouped.has(cat)).map((cat) => {
      const meta = FIX_CATEGORY_META[cat];
      const fixes = grouped.get(cat)!;
      return {
        key: cat,
        label: (
          <div className={`kuika_chatbot__fix-group-header kuika_chatbot__fix-group-header--${cat.toLowerCase()}`}>
            <span className={`kuika_chatbot__fix-badge kuika_chatbot__fix-badge--${cat.toLowerCase()}`}>
              <KuikaSvg type="common" name={meta.iconName} width={12} height={12} />
              <span>{meta.label}</span>
            </span>
            <span className="kuika_chatbot__fix-group-count">{fixes.length}</span>
          </div>
        ),
        children: (
          <div className="kuika_chatbot__fix-group-body">
            {fixes.map((fix, idx) => (
              <KuikaInfoBox
                key={`${cat}-${idx}`}
                color={meta.color}
                leftUnit={<div className="kuika_chatbot__fix-text">{fix.text}</div>}
              />
            ))}
          </div>
        )
      };
    });

    // Default: open the highest-priority group (first in display order)
    const defaultActiveKey = items.length ? [items[0].key] : [];

    return (
      <div className="kuika_chatbot__fix-list">
        <KuikaCollapsePanel animated iconPlacement="right" defaultActiveKey={defaultActiveKey} items={items} />
      </div>
    );
  };

  render() {
    return (
      <div className="kuika_chatbot">
        <div className="kuika_chatbot__container">
          <div className="kuika_chatbot__messages-container" ref={this.props.messagesContainerRef}>
            {this.props.initMessage &&
              this.renderMessage({ content: this.props.initMessage, role: EOpenAIUserRole.Assistant })}

            {this.props.messages
              .filter((message) => !this.isConfigSummaryMessage(message))
              .map((message) => this.renderMessage(message))}
          </div>
          <div className="kuika_chatbot__form-container">
            <Form ref={this.formRef} onFinish={this.onMessageSend}>
              <Form.Item
                name="userMessage"
                rules={[{ required: true, message: "Please type message to generate code." }]}
                noStyle
              >
                <div className="kuika_chatbot__textarea-container">
                  <KuikaInput
                    type="textarea"
                    ref={this.textareaRef}
                    placeholder={this.props.inputPlaceholder}
                    borderless
                    resizable={false}
                    onKeyDown={this.handleInputOnEnter}
                    onFocus={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      e.target.focus();
                    }}
                    autoSize={{ minRows: 2, maxRows: 4 }}
                  />
                  {this.props.isLoading ? (
                    <>
                      <SpinSvg className="spin-icon" />
                    </>
                  ) : (
                    <>
                      {this.props.inputRightIcon ? (
                        this.props.inputRightIcon
                      ) : (
                        <KuikaButton
                          className="kuika_chatbot__send-button"
                          type="tertiary"
                          color="neutral"
                          size="sm"
                          isIcon
                          onClick={() => {
                            this.formRef.current?.submit();
                          }}
                        >
                          <KuikaSvg type="arrows" name="arrow-up" color="var(--blue-5)" />
                        </KuikaButton>
                      )}
                    </>
                  )}
                </div>
              </Form.Item>
            </Form>
          </div>
        </div>
      </div>
    );
  }
}
